// frontend/modules/sms/hooks/useSms.ts
import { useCallback, useEffect } from 'react';
import { useTheme } from '@/components/common/ThemeProvider';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import {useGetSmsBalance, useSendSingleSms} from "@/shared/api/client/sms-client-controller";
import {useSendMultipleMessages} from "@/shared/api/client/sms-client-controller";
import {useSmsStore} from "@/modules/sms/stores/sms-store";

// Import the Orval generated API client - assuming it will be generated
// based on your SmsClientController


export const useSms = () => {
    const { dark } = useTheme();

    // Get state and actions from store
    const {
        message,
        recipients,
        senderId,
        reference,
        isMultiMode,
        isSending,
        smsBalance,
        isLoadingBalance,

        setMessage,
        setSenderId,
        setReference,
        setIsMultiMode,
        addRecipient,
        removeRecipient,
        updateRecipient,
        clearForm,
        setIsSending,
        setSmsBalance,
        setIsLoadingBalance
    } = useSmsStore();

    // Get mutations and queries from Orval generated hooks
    const { mutateAsync: sendSingleSms, isPending: isSendingSingle } = useSendSingleSms();
    const { mutateAsync: sendMultiMessages, isPending: isSendingMulti } = useSendMultipleMessages();
    const { data: balanceData, isLoading: isLoadingBalanceData, refetch: refetchBalance } = useGetSmsBalance();

    // Update isSending state based on mutations
    useEffect(() => {
        setIsSending(isSendingSingle || isSendingMulti);
    }, [isSendingSingle, isSendingMulti, setIsSending]);

    // Update balance when data is loaded
    useEffect(() => {
        if (balanceData) {
            setSmsBalance(balanceData.remainingBalance ?? 0);
        }
    }, [balanceData, setSmsBalance]);

    // Update isLoadingBalance
    useEffect(() => {
        setIsLoadingBalance(isLoadingBalanceData);
    }, [isLoadingBalanceData, setIsLoadingBalance]);

    // Load SMS balance
    const loadSmsBalance = useCallback(() => {
        refetchBalance();
    }, [refetchBalance]);

    // Initialize - load balance
    useEffect(() => {
        loadSmsBalance();
    }, [loadSmsBalance]);

    // Send SMS - handles both single and multiple
    const sendSms = useCallback(async () => {
        // Validate message
        if (!message.trim()) {
            showErrorNotification('Error', 'Message cannot be empty', dark);
            return;
        }

        // Validate sender ID
        if (!senderId.trim()) {
            showErrorNotification('Error', 'Sender ID cannot be empty', dark);
            return;
        }

        // Validate recipients
        const validRecipients = recipients.filter(r => r.valid);
        if (validRecipients.length === 0) {
            showErrorNotification('Error', 'No valid recipients provided', dark);
            return;
        }

        try {
            let response;

            if (isMultiMode) {
                // Handle multiple messages with different content (not implemented in UI yet)
                showErrorNotification('Error', 'Multi-message mode is not yet implemented in UI', dark);
                return;
            } else {
                // Simple mode - same message to all recipients
                if (validRecipients.length === 1) {
                    // Single recipient
                    response = await sendSingleSms({
                        data: {
                            senderId,
                            phoneNumber: validRecipients[0].phoneNumber,
                            text: message,
                            reference
                        }
                    });

                    showSuccessNotification(
                        'SMS Sent',
                        `Message sent successfully to ${validRecipients[0].phoneNumber}`,
                        dark
                    );
                } else {
                    // Multiple recipients with the same message
                    response = await sendMultiMessages({
                        data: {
                            messages: [{
                                senderId,
                                recipients: validRecipients.map(r => r.phoneNumber),
                                text: message
                            }],
                            reference
                        }
                    });

                    showSuccessNotification(
                        'SMS Sent',
                        `Message sent successfully to ${validRecipients.length} recipients`,
                        dark
                    );
                }
            }

            console.log('SMS sent response:', response);

            // Clear form and refresh balance
            clearForm();
            loadSmsBalance();

        } catch (error) {
            console.error('Error sending SMS:', error);
            showErrorNotification(
                'Error',
                'Failed to send SMS. Please try again later.',
                dark
            );
        }
    }, [
        message,
        senderId,
        recipients,
        reference,
        isMultiMode,
        sendSingleSms,
        sendMultiMessages,
        clearForm,
        loadSmsBalance,
        dark
    ]);

    return {
        // State
        message,
        recipients,
        senderId,
        reference,
        isMultiMode,
        isSending,
        smsBalance,
        isLoadingBalance,

        // Actions
        setMessage,
        setSenderId,
        setReference,
        setIsMultiMode,
        addRecipient,
        removeRecipient,
        updateRecipient,
        clearForm,
        sendSms,
        loadSmsBalance
    };
};